<template>
  <div
    class="race-video-gps-pane"
    :class="[
      `race-video-gps-pane--${screenOrientationType}`,
      `race-video-gps-pane--mode-${gpsViewMode}`,
    ]"
  >
    <race-player-info-section
      :radioVoiceEnabled="radioVoiceEnabled"
      :raceSelectEnabled="raceSelectEnabled"
      :screenOrientationType="screenOrientationType"
      :favoriteRacePlayerSelected="favoriteRacePlayerSelected"
      @radioVoiceToggle="toggleRadioVoice"
      @raceSelectToggle="toggleRaceSelect"
      @playerChange="showPlayerSelectModal"
    />

    <div class="race-video-gps-pane__gps-content">
      <gps-details-map-section
        v-if="gpsViewMode === 'birdsEye'"
        class="race-video-gps-pane__map"
        :gpsViewMode="gpsViewMode"
        :circuitName="circuitName"
        :circuitGpsPosition="circuitGpsPosition"
        :animateGpsMapCar="animateGpsMapCar"
        :mapMagnification="mapMagnification"
        :screenOrientationType="screenOrientationType"
        :lockOrientation="lockOrientation"
      />

      <gps-details-map-section
        v-if="gpsViewMode === 'battle'"
        class="race-video-gps-pane__map"
        key="race-video-gps-battle-map"
        :gpsViewMode="gpsViewMode"
        :animateGpsMapCar="animateGpsMapCar"
        :circuitName="circuitName"
        :circuitGpsPosition="circuitGpsPosition"
        :mapMagnification="mapMagnification"
        :screenOrientationType="screenOrientationType"
        :lockOrientation="lockOrientation"
      />

      <gps-details-map-section
        v-if="gpsViewMode === 'focus'"
        class="race-video-gps-pane__map"
        key="race-video-gps-focus-map"
        :gpsViewMode="gpsViewMode"
        :animateGpsMapCar="animateGpsMapCar"
        :circuitName="circuitName"
        :circuitGpsPosition="circuitGpsPosition"
        :mapMagnification="mapMagnification"
        :screenOrientationType="screenOrientationType"
        :lockOrientation="lockOrientation"
      />

      <map-scale-parts
        v-if="!screenLandscape"
        class="race-video-gps-pane__scale-gauge"
        :gpsViewMode="gpsViewMode"
        :mapMagnification="mapMagnification"
      />

      <gps-view-mode-selector-section
        class="race-video-gps-pane__mode-selector"
        :gpsViewMode="gpsViewMode"
        :screenOrientationType="screenOrientationType"
        @onclickSelector="onclickSelector"
      />

      <gps-ranking-section
        v-if="!screenLandscape"
        class="race-video-gps-pane__ranking"
        :rankingData="getCurrentPlayerRankingDataList"
        :selectedID="selectedID"
      />
    </div>

    <player-select-modal-section
      class="race-video-gps-pane__player-select"
      v-show="racePlayerSelectModal"
      :screenOrientationType="screenOrientationType"
      :favoriteRacePlayerSelected="favoriteRacePlayerSelected"
      @close="changePlayer"
    />

    <race-select-section
      v-show="raceSelectEnabled"
      :screenOrientationType="screenOrientationType"
      @raceSelectToggle="toggleRaceSelect"
      @changeScenes="changeScenes"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, toRefs } from '@vue/composition-api'
import RacePlayerInfoSection from '@/components/RaceVideoPage/RaceVideoDataViewPane/RacePlayerInfoSection.vue'
import PlayerSelectModalSection from '@/components/RaceVideoPage/RaceVideoDataViewPane/PlayerSelectModalSection.vue'
import RaceSelectSection from '@/components/RaceVideoPage/RaceVideoDataViewPane/RaceSelectSection.vue'
import GpsViewModeSelectorSection from '@/components/RaceVideoPage/RaceVideoGpsPane/GpsViewModeSelectorSection.vue'
import GpsDetailsMapSection from '@/components/RaceVideoPage/RaceVideoGpsPane/GpsDetailsMapSection.vue'
import GpsRankingSection from '@/components/RaceVideoPage/RaceVideoGpsPane/GpsRankingSection.vue'
import MapScaleParts from '@/components/RaceVideoPage/RaceVideoGpsPane/parts/MapScaleParts.vue'
import StoreUtil from '@/store/StoreUtil'
import Const from '@/util/Const'
import { CircuitGpsPosition } from '@/components/RaceVideoPage/RaceVideoPane/CircuitMapSection.vue'
import Logger from '@/util/logger/Logger'
import useGps from '@/components/RaceVideoPage/hook/useGps'
import { LockOrientationType } from '@/views/RaceVideoPage/RaceVideoPage.vue'

/**
 * GPS表示モード
 */
export type GpsViewModeType = 'birdsEye' | 'battle' | 'focus'

/**
 * レース動画再生画面 GPS詳細マップペインのコンポーネント
 */
export default defineComponent({
  name: 'RaceVideoGpsPane',
  components: {
    RacePlayerInfoSection,
    PlayerSelectModalSection,
    RaceSelectSection,
    GpsViewModeSelectorSection,
    GpsDetailsMapSection,
    GpsRankingSection,
    MapScaleParts,
  },
  props: {
    /**
     * 無線音声 ON/OFFフラグ
     */
    radioVoiceEnabled: {
      type: Boolean,
      default: false,
    },
    /**
     * レース選択 開閉フラグ
     */
    raceSelectEnabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 画面向き
     */
    screenOrientationType: {
      type: String,
      default: 'portrait-primary',
    },
    /**
     * 画面固定
     */
    lockOrientation: {
      type: String as PropType<LockOrientationType>,
    },
    /**
     * 選手選択モーダル表示フラグ
     */
    racePlayerSelectModal: {
      type: Boolean,
      default: false,
    },
    /**
     * 車両データアニメーションフラグ
     */
    animateGpsMapCar: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const screenLandscape = computed(() => props.screenOrientationType?.indexOf('landscape') !== -1)

    const raceVideoPageStore = StoreUtil.useStore('RaceVideoPageStore')
    const { getCurrentPlayerRankingDataList } = useGps()

    const {
      setViewAngle,
      user,
      isLiveNotStarted,
      fetchRaceData,
      currentRaceId,
      highlightStore,
      targetChampionship,
    } = raceVideoPageStore
    const { selectedHighlightId } = highlightStore
    const { videoPlayer } = toRefs(raceVideoPageStore.raceVideoPageState)
    const favoriteRacePlayerSelected = computed(() => !!user.value?.favoritePlayerId)
    const selectedID = computed(() => raceVideoPageStore.raceVideoPageState.selectedPlayerId)

    const updateCurrentTime = (currentTime: number) => {
      videoPlayer.value?.setCurrentTime(currentTime)
    }

    /**
     * GPS表示モード
     * 'birdsEye' or 'battle' or 'focus'
     */
    const gpsViewMode = ref<GpsViewModeType>('birdsEye')

    /**
     * GPS詳細表示モード変更
     * @param changeMode
     */
    const onclickSelector = (changeMode: GpsViewModeType) => {
      gpsViewMode.value = changeMode
    }

    /**
     * 現在選択されているレースのサーキット
     */
    const currentCircuit = targetChampionship.value.course
    if (!currentCircuit) {
      Logger.warn('Failed to get circuit info')
    }

    /**
     * サーキットMAP GPS 座標
     */
    const circuitGpsPosition: CircuitGpsPosition = currentCircuit
      ? Const.GPS_CIRCUIT_MAP_GPS[currentCircuit]
      : Const.GPS_CIRCUIT_MAP_GPS.UNKNOWN

    /**
     * サーキット名取得
     */
    const circuitName = currentCircuit

    /**
     * MAP 表示倍率
     */
    const mapMagnification = computed(() => {
      let rt: number
      if (gpsViewMode.value === 'battle') {
        rt = Const.GPS_MAP_MAGNIFICATION.BATTLE
      } else if (gpsViewMode.value === 'focus') {
        rt = Const.GPS_MAP_MAGNIFICATION.FOCUS
      } else {
        rt = Const.GPS_MAP_MAGNIFICATION.BIRDSEYE
      }
      return rt
    })

    return {
      gpsViewMode,
      onclickSelector,
      favoriteRacePlayerSelected,
      setViewAngle,
      selectedID,
      isLiveNotStarted,
      raceVideoPageStore,
      fetchRaceData,
      selectedHighlightId,
      currentRaceId,
      videoPlayer,
      updateCurrentTime,
      getCurrentPlayerRankingDataList,
      mapMagnification,
      circuitName,
      circuitGpsPosition,
      screenLandscape,
    }
  },
  methods: {
    /**
     * 無線音声 ON/OFFボタンが押下されたことを通知する
     */
    toggleRadioVoice(): void {
      this.$emit('radioVoiceToggle')
    },

    /**
     * レース選択ボタンが押下されたことを通知する
     */
    toggleRaceSelect(state?: string): void {
      this.$emit('raceSelectToggle', state)
    },

    /**
     * 選手選択モーダル表示
     */
    showPlayerSelectModal(): void {
      this.$emit('playerSelectModalShow', true)
    },

    /**
     * 選択選手変更処理
     * @event changePlayer
     * @param submitType
     */
    changePlayer(submitType: string) {
      if (submitType === 'change') {
        // 選手を選択した場合、自動的にアングルを選手に変更する
        this.setViewAngle('player')
        // 視聴履歴取得
        this.raceVideoPageStore.saveHistory()
      }
      this.$emit('playerSelectModalShow', false)
    },
    changeScenes(raceId: string, highlightId: string) {
      if (this.currentRaceId !== raceId) {
        this.currentRaceId = raceId
      }
      if (highlightId) {
        this.selectedHighlightId = highlightId
      } else {
        this.updateCurrentTime(0)
      }
    },

    /**
     * 無線交信データ一覧のドライバー選択が変更された場合に呼び出される。
     * @param id ドライバーID
     */
    changeDriverFilter(id: string): void {
      this.$emit('changeDriverFilter', id)
    },

    /**
     * 無線交信データ一覧のLAPの選択が変更された場合に呼び出される。
     * @param id LAP数(周回数)
     */
    changeLapFilter(id: string): void {
      this.$emit('changeLapFilter', id)
    },
  },
})
</script>

<style lang="scss" scoped>
.race-video-gps-pane {
  --gps-content-height: calc(var(--screen-height) - 56.25vw);
  --gps-content-bottom-padding: calc(var(--safe-area-inset-bottom) * 0.8 + 44px);

  position: relative;
  width: 100%;
  height: var(--gps-content-height);
  margin: 0 auto;

  &__gps-content {
    position: fixed;
    z-index: 1;
    display: flex;
    flex-flow: column nowrap;
    align-items: center;
    align-content: center;
    justify-items: flex-end;
    justify-content: flex-end;
    width: 100%;
    gap: 12px;
    height: calc(var(--gps-content-height) - 44px);
    padding-bottom: var(--gps-content-bottom-padding);
    overflow: hidden;
  }

  &__scale-gauge,
  &__mode-selector,
  &__ranking {
    position: relative;
    z-index: 2;
  }

  &__map {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 0;
    width: 100vw;
    transform: translate(0, calc((var(--gps-content-bottom-padding) + 56px) * -1));
  }

  &--mode-birdsEye {
    --gps-birdseye-map-max-width: calc(
      (var(--gps-content-height) - var(--gps-content-bottom-padding) - 216px) / 9 * 16
    );

    .race-video-gps-pane__map {
      max-width: var(--gps-birdseye-map-max-width);
      transform: translate(-50%, calc(-50% - (var(--gps-content-bottom-padding) + 56px)));
    }
  }

  @media screen and (max-height: 680px), screen and (max-width: 320px) {
    &--mode-birdsEye {
      .race-video-gps-pane__map {
        transform: translate(-50%, calc(-50% - (var(--gps-content-bottom-padding) + 38px)));
      }
    }
  }

  &--landscape-primary,
  &--landscape-secondary {
    height: 100vh;

    .race-video-gps-pane {
      &__gps-content {
        position: absolute;
        top: 44px;
        left: 0;
        right: 0;
        bottom: 0;
        height: auto;
        padding-bottom: 4px;
      }

      &__map {
        position: absolute;
        top: 50%;
        left: 50%;
        z-index: 0;
        width: 100%;
        transform: translate(0, -18px);
      }

      &__player-select {
        position: absolute;
      }
    }

    &.race-video-gps-pane {
      &--mode-birdsEye {
        .race-video-gps-pane__map {
          width: 100%;
          max-width: 375px;
          transform: translate(-50%, calc(-50% - 18px));
        }
      }
    }
  }
}
</style>
